import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Image, Download, Trash2, Copy, RefreshCw, Loader2,
  ArrowLeft, Sparkles, Grid, List, Search, Filter,
  CheckCircle, AlertCircle, Eye, Palette
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import * as api from '../services/api';

const TECH_FONT = "'Inter', 'SF Pro Display', sans-serif";

const DesignerGalleryPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (authLoading) return;
    
    // Verificar que sea diseñador, editor o admin
    const hasAccess = user && (
      user.user_type === 'admin' || 
      user.user_type === 'staff' ||
      ['admin', 'editor', 'disenador'].includes(user.staff_role)
    );
    
    if (!hasAccess) {
      navigate('/');
      return;
    }
    
    fetchImages();
  }, [user, authLoading, navigate]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await api.getGeneratedImages();
      setImages(response || []);
    } catch (err) {
      console.error('Error fetching images:', err);
      setError('Error al cargar las imágenes');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!generatePrompt.trim()) {
      setError('Por favor escribe una descripción para la imagen');
      return;
    }

    setGenerating(true);
    setError('');

    try {
      const response = await api.generateStandaloneImage({
        prompt: generatePrompt,
        category: 'design'
      });
      
      setSuccess('¡Imagen generada exitosamente!');
      setShowGenerateModal(false);
      setGeneratePrompt('');
      fetchImages(); // Refresh the list
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al generar la imagen');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadImage = (imageUrl, imageName) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = imageName || 'imagen-generada.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyUrl = (imageUrl) => {
    navigator.clipboard.writeText(imageUrl);
    setSuccess('URL copiada al portapapeles');
    setTimeout(() => setSuccess(''), 2000);
  };

  const filteredImages = searchTerm.trim() 
    ? images.filter(img => 
        img.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        img.prompt?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        img.dossier_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        img.prompt_used?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : images;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="designer-gallery-page">
      {/* Header */}
      <div className="bg-[#0f172a] text-white py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-4 mb-2">
                <Link to="/" className="text-gray-400 hover:text-cyan-400 transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </Link>
                <h1 className="text-2xl font-bold" style={{ fontFamily: TECH_FONT }}>
                  Galería de Imágenes
                </h1>
              </div>
              <p className="text-gray-400 text-sm ml-9">
                Gestiona y genera imágenes para tus noticias
              </p>
            </div>
            <Button 
              onClick={() => setShowGenerateModal(true)}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="generate-new-image-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generar Nueva Imagen
            </Button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            {/* Search */}
            <div className="flex-1 max-w-md relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar imágenes..."
                className="pl-10"
                data-testid="image-search-input"
              />
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow-sm' : ''}`}
                data-testid="grid-view-btn"
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
                data-testid="list-view-btn"
              >
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Stats */}
            <Badge className="bg-slate-100 text-slate-700">
              {filteredImages.length} imágenes
            </Badge>
          </div>
        </div>
      </div>

      {/* Alerts */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            {error}
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">×</button>
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            {success}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : filteredImages.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Image className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay imágenes</h3>
            <p className="text-gray-500 mb-6">
              {searchTerm ? 'No se encontraron imágenes con ese término' : 'Genera tu primera imagen con IA'}
            </p>
            <Button 
              onClick={() => setShowGenerateModal(true)}
              className="bg-[#0f172a] hover:bg-slate-800"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Generar Imagen
            </Button>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredImages.map((image) => (
              <div
                key={image.image_id}
                className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => setSelectedImage(image)}
                data-testid={`image-card-${image.image_id}`}
              >
                <div className="aspect-square bg-gray-100">
                  <img
                    src={image.url || `${process.env.REACT_APP_BACKEND_URL}/api/images/${image.image_id}`}
                    alt={image.title || 'Imagen generada'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400';
                    }}
                  />
                </div>
                
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); setSelectedImage(image); }}>
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); handleCopyUrl(image.url); }}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {image.title || image.dossier_title || 'Sin título'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {image.created_at ? new Date(image.created_at).toLocaleDateString('es') : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Imagen</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Título</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Artículo</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredImages.map((image) => (
                  <tr key={image.image_id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={image.url || `${process.env.REACT_APP_BACKEND_URL}/api/images/${image.image_id}`}
                          alt={image.title}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=100';
                          }}
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{image.title || 'Sin título'}</p>
                      <p className="text-sm text-gray-500 truncate max-w-xs">{image.prompt}</p>
                    </td>
                    <td className="px-6 py-4">
                      {image.dossier_id ? (
                        <Link 
                          to={`/dossiers/${image.dossier_id}`}
                          className="text-cyan-600 hover:text-cyan-700 text-sm"
                        >
                          {image.dossier_title || 'Ver artículo'}
                        </Link>
                      ) : (
                        <span className="text-gray-400 text-sm">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {image.created_at ? new Date(image.created_at).toLocaleDateString('es') : '—'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedImage(image)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleCopyUrl(image.url)}>
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDownloadImage(image.url, image.title)}>
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Image Preview Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selectedImage?.title || 'Vista previa'}</DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4">
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={selectedImage.url || `${process.env.REACT_APP_BACKEND_URL}/api/images/${selectedImage.image_id}`}
                  alt={selectedImage.title}
                  className="w-full h-full object-contain"
                />
              </div>
              
              {selectedImage.prompt && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-1">Prompt usado:</p>
                  <p className="text-sm text-gray-600">{selectedImage.prompt}</p>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t">
                <div className="text-sm text-gray-500">
                  {selectedImage.created_at && `Creada: ${new Date(selectedImage.created_at).toLocaleString('es')}`}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleCopyUrl(selectedImage.url)}>
                    <Copy className="w-4 h-4 mr-2" />
                    Copiar URL
                  </Button>
                  <Button onClick={() => handleDownloadImage(selectedImage.url, selectedImage.title)}>
                    <Download className="w-4 h-4 mr-2" />
                    Descargar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Generate Image Modal */}
      <Dialog open={showGenerateModal} onOpenChange={setShowGenerateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Generar Nueva Imagen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe la imagen que quieres generar
              </label>
              <textarea
                value={generatePrompt}
                onChange={(e) => setGeneratePrompt(e.target.value)}
                placeholder="Ej: Una imagen futurista de inteligencia artificial con circuitos brillantes y tonos azules..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                data-testid="generate-prompt-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sé específico con los colores, estilo y elementos que deseas en la imagen
              </p>
            </div>

            <div className="bg-cyan-50 p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Palette className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-cyan-900">Tips para mejores resultados:</p>
                  <ul className="text-sm text-cyan-700 mt-1 space-y-1">
                    <li>• Menciona el estilo (fotográfico, ilustración, minimalista)</li>
                    <li>• Especifica colores dominantes</li>
                    <li>• Describe la composición deseada</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowGenerateModal(false)} 
                className="flex-1"
                disabled={generating}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleGenerateImage}
                className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                disabled={generating || !generatePrompt.trim()}
                data-testid="submit-generate-btn"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generar Imagen
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignerGalleryPage;
